// ═══════════════════════════════════════════════════════════════════════
// Part 8: Export Engine
// ═══════════════════════════════════════════════════════════════════════
// Shared pipeline for building final export rows, runtime validation,
// CSV/XLSX generation, and browser download.
//
// This engine is used by both OutputPreview and the final export action.
// It resolves all template columns using the same logic: transforms,
// calculated columns, margin rules, and invoice data.

import * as XLSX from 'xlsx';
import type {
  ExportJobRow,
  ExportTemplateColumn,
  AddressAliasRule,
  MarginRule,
  InvoiceAssignment,
  InvoiceRunSummary,
  RowAnomalyResult,
  AnomalySummary,
  AnomalyReviewStatus,
  TownClassification,
} from './types';
import { resolveColumnValueWithMargins, augmentRowWithInvoiceData } from './transforms';


// ─── Export Scope ───────────────────────────────────────────────────

export type ExportScope =
  | 'all'                    // All filtered rows
  | 'exclude_ignored'        // Exclude rows with ignored anomalies
  | 'exclude_unreviewed'     // Exclude rows with unreviewed anomalies
  | 'only_clean';            // Only rows with no anomalies or reviewed anomalies

export const EXPORT_SCOPE_INFO: Record<ExportScope, { label: string; description: string }> = {
  all: {
    label: 'All Filtered Rows',
    description: 'Export every row in the current filtered dataset',
  },
  exclude_ignored: {
    label: 'Exclude Ignored',
    description: 'Skip rows where anomalies have been marked as ignored',
  },
  exclude_unreviewed: {
    label: 'Exclude Unreviewed Anomalies',
    description: 'Skip rows with unreviewed anomaly flags',
  },
  only_clean: {
    label: 'Only Clean / Reviewed',
    description: 'Only export rows with no anomalies or reviewed anomalies',
  },
};

// ─── Export File Type ───────────────────────────────────────────────

export type ExportFileType = 'csv' | 'xlsx';

// ─── Export Settings ────────────────────────────────────────────────

export interface ExportSettings {
  /** File type to generate */
  fileType: ExportFileType;
  /** Custom file name (without extension) */
  fileName: string;
  /** Row scope filter */
  scope: ExportScope;
  /** Include column headers in output */
  includeHeaders: boolean;
  /** Sheet name for XLSX files */
  sheetName: string;
  /** Insert a blank row between each invoice number group (legacy flag) */
  blankRowBetweenInvoices?: boolean;
  /**
   * Part 17: Generalized blank-row grouping.
   * When set (and blankRowBetweenInvoices is true), insert a blank row between
   * groups of rows that share the same value for this source field.
   *
   * Special values:
   *   'invoice'  — use the assigned invoice number (requires invoiceAssignments)
   *   <field>    — any ExportJobRow field name (e.g. 'account_id', 'pu_date',
   *                'passenger_name', 'job_id')
   *
   * When undefined, defaults to 'invoice' if invoice assignments exist.
   */
  blankRowGroupByField?: string;
}


// ─── Validation Types ───────────────────────────────────────────────

export type ValidationSeverity = 'error' | 'warning';

export interface ValidationIssue {
  severity: ValidationSeverity;
  code: string;
  message: string;
  detail?: string;
}

export interface ValidationResult {
  isValid: boolean;
  hasBlockingErrors: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  finalRowCount: number;
  finalColumnCount: number;
}

// ─── Export Summary ─────────────────────────────────────────────────

export interface ExportSummaryData {
  templateName: string;
  fileType: ExportFileType;
  fileName: string;
  rowCount: number;
  columnCount: number;
  startingInvoice: number | null;
  endingInvoice: number | null;
  invoiceGroupCount: number | null;
  activeMarginRules: number;
  anomalyIncluded: number;
  anomalyExcluded: number;
  totalGrandTotal: number;
  scope: ExportScope;
}

// ─── Helper: Filter out excludeFromExport columns ───────────────────

/**
 * Returns only the columns that should appear in the final export output.
 * Columns with transform_config.excludeFromExport === true are filtered out.
 * These columns are still resolved during row building (for calc references)
 * but their values are stripped from the final output.
 */
function getExportableColumns(columns: ExportTemplateColumn[]): ExportTemplateColumn[] {
  return columns
    .filter(c => c.is_enabled && !c.transform_config?.excludeFromExport)
    .sort((a, b) => a.column_order - b.column_order);
}

/**
 * Returns ALL enabled columns (including excludeFromExport) sorted by order.
 * Used internally for resolving values (calc column references need all columns).
 */
function getAllEnabledColumns(columns: ExportTemplateColumn[]): ExportTemplateColumn[] {
  return columns
    .filter(c => c.is_enabled)
    .sort((a, b) => a.column_order - b.column_order);
}

const EXCEL_FORMULA_MARKER_PREFIX = '__EXCEL_FORMULA__::';

interface ExcelFormulaMarkerPayload {
  template: string;
  resultType: 'number' | 'string';
}

function encodeExcelFormulaMarker(template: string, resultType: 'number' | 'string' = 'number'): string {
  return `${EXCEL_FORMULA_MARKER_PREFIX}${JSON.stringify({ template, resultType })}`;
}

function parseExcelFormulaMarker(value: string): ExcelFormulaMarkerPayload | null {
  if (!value || !value.startsWith(EXCEL_FORMULA_MARKER_PREFIX)) return null;
  try {
    return JSON.parse(value.slice(EXCEL_FORMULA_MARKER_PREFIX.length)) as ExcelFormulaMarkerPayload;
  } catch {
    return null;
  }
}

function columnIndexToExcelLetter(index: number): string {
  let n = index + 1;
  let result = '';
  while (n > 0) {
    const remainder = (n - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    n = Math.floor((n - 1) / 26);
  }
  return result;
}

function buildHeaderLetterMap(headers: string[]): Map<string, string> {
  const map = new Map<string, string>();
  headers.forEach((header, idx) => map.set(header, columnIndexToExcelLetter(idx)));
  return map;
}

function resolveExcelFormulaTemplate(
  template: string,
  rowNumber: number,
  headerLetterMap: Map<string, string>
): string {
  const body = (template || '').trim().replace(/^=/, '');
  return body.replace(/{{\s*([^}]+?)\s*}}/g, (_match, token) => {
    const letter = headerLetterMap.get(String(token).trim());
    return letter ? `${letter}${rowNumber}` : '0';
  });
}

// ─── Apply Export Scope Filter ──────────────────────────────────────

/**
 * Filter rows based on the export scope and anomaly results.
 * Returns the rows that should be included in the export.
 */
export function applyExportScope(
  rows: ExportJobRow[],
  scope: ExportScope,
  anomalyResults: Map<string, RowAnomalyResult> | null
): { included: ExportJobRow[]; excluded: number } {
  if (scope === 'all' || !anomalyResults || anomalyResults.size === 0) {
    return { included: rows, excluded: 0 };
  }

  let excluded = 0;
  const included = rows.filter(row => {
    const anomaly = anomalyResults.get(row.tc_id);
    if (!anomaly || anomaly.flags.length === 0) return true; // No anomalies, always include

    switch (scope) {
      case 'exclude_ignored':
        if (anomaly.reviewStatus === 'ignored') { excluded++; return false; }
        return true;

      case 'exclude_unreviewed':
        if (anomaly.reviewStatus === 'flagged') { excluded++; return false; }
        return true;

      case 'only_clean':
        if (anomaly.reviewStatus !== 'reviewed') { excluded++; return false; }
        return true;

      default:
        return true;
    }
  });

  return { included, excluded };
}

// ─── Build Export Rows ──────────────────────────────────────────────

/**
 * Build final export-ready rows from the dataset.
 * This is the shared pipeline used by both preview and final export.
 *
 * For each row:
 * 1. Augment with invoice data (if available)
 * 2. Resolve each enabled template column in column_order using transforms,
 *    calculated columns, margin rules — building up a resolvedCalcColumns map
 *    so that calculated columns referencing other calculated columns work correctly
 * 3. Return plain key-value objects keyed by export column name
 *
 * IMPORTANT: Columns with transform_config.excludeFromExport === true are still
 * resolved (so calculated columns can reference them) but are NOT included in the
 * final output rows. Only "exportable" columns appear in the returned records.
 *
 * Returns clean string/number values suitable for CSV/XLSX output.
 */
export function buildExportRows(
  rows: ExportJobRow[],
  columns: ExportTemplateColumn[],
  aliasRules: AddressAliasRule[],
  marginRules: MarginRule[],
  invoiceAssignments: Map<string, InvoiceAssignment> | null,
  townClassifications?: Map<string, TownClassification>,
  rangeEndDate?: string
): Record<string, string>[] {
  // ALL enabled columns — needed for resolving calc references
  const allEnabledCols = getAllEnabledColumns(columns);

  // Set of column IDs that should be excluded from output
  const excludedColIds = new Set(
    allEnabledCols
      .filter(c => c.transform_config?.excludeFromExport)
      .map(c => c.id)
  );

  return rows.map(row => {
    // Augment with invoice data
    const assignment = invoiceAssignments?.get(row.tc_id);
    const augmentedRow = augmentRowWithInvoiceData(row, assignment);

    // Build a map of resolved calculated column values for this row.
    // Keyed by column ID so that __calc_col__::<id> references can resolve.
    const resolvedCalcColumns = new Map<string, string>();

    const outputRow: Record<string, string> = {};
    for (const col of allEnabledCols) {
      if (col.source_type === 'calculated' && col.transform_config?.calcType === 'excel_formula') {
        const formulaTemplate = col.transform_config?.excelFormulaTemplate || '';
        const resultType = col.transform_config?.excelFormulaResultType === 'string' ? 'string' : 'number';
        const encoded = encodeExcelFormulaMarker(formulaTemplate, resultType);

        if (!excludedColIds.has(col.id)) {
          outputRow[col.export_column_name] = encoded;
        }

        // Excel formulas are resolved later during XLSX generation.
        // For downstream in-app calculated references, treat them as blank.
        resolvedCalcColumns.set(col.id, '');
        continue;
      }

      const { value } = resolveColumnValueWithMargins(
        col, augmentedRow, aliasRules, marginRules, townClassifications, resolvedCalcColumns, rangeEndDate
      );

      // Only include in output if NOT excluded from export
      if (!excludedColIds.has(col.id)) {
        outputRow[col.export_column_name] = value;
      }

      // After resolving a calculated column, store its final value
      // so subsequent columns can reference it via __calc_col__::<id>
      if (col.source_type === 'calculated') {
        resolvedCalcColumns.set(col.id, value);
      }
    }
    return outputRow;
  });
}



/**
 * Get the ordered list of column headers for the export.
 * Excludes columns with transform_config.excludeFromExport === true.
 */
export function getExportHeaders(columns: ExportTemplateColumn[]): string[] {
  return getExportableColumns(columns).map(c => c.export_column_name);
}

// ─── Sort Export Rows by Invoice Number ─────────────────────────────

/**
 * Sort export rows by invoice number so that rows sharing the same
 * invoice number are grouped together. Rows without an invoice
 * assignment are placed at the end.
 *
 * Both `exportRows` and `sourceRows` must be in the same order (1:1).
 * Returns new arrays — the originals are not mutated.
 */
export function sortExportRowsByInvoice(
  exportRows: Record<string, string>[],
  sourceRows: ExportJobRow[],
  invoiceAssignments: Map<string, InvoiceAssignment> | null
): { sortedExportRows: Record<string, string>[]; sortedSourceRows: ExportJobRow[] } {
  if (!invoiceAssignments || invoiceAssignments.size === 0 || exportRows.length <= 1) {
    return { sortedExportRows: exportRows, sortedSourceRows: sourceRows };
  }

  // Build paired index array and sort by invoice number
  const indices = Array.from({ length: exportRows.length }, (_, i) => i);
  indices.sort((a, b) => {
    const srcA = sourceRows[a];
    const srcB = sourceRows[b];
    const invA = srcA ? invoiceAssignments.get(srcA.tc_id)?.assignedInvoiceNumber ?? Infinity : Infinity;
    const invB = srcB ? invoiceAssignments.get(srcB.tc_id)?.assignedInvoiceNumber ?? Infinity : Infinity;
    return invA - invB;
  });

  const sortedExportRows = indices.map(i => exportRows[i]);
  const sortedSourceRows = indices.map(i => sourceRows[i]);
  return { sortedExportRows, sortedSourceRows };
}

// ─── Insert Blank Rows Between Invoice Groups ───────────────────────

/**
 * Insert blank separator rows between groups of rows that share the same
 * invoice number. This makes the exported file easier to read when printed
 * or viewed in Excel.
 *
 * IMPORTANT: This function now sorts rows by invoice number first so that
 * all rows for the same invoice are grouped together before separators
 * are inserted.
 *
 * @param exportRows - The built export rows (after buildExportRows)
 * @param sourceRows - The original ExportJobRow[] in the same order
 * @param invoiceAssignments - Invoice assignments map (tc_id -> assignment)
 * @param headers - Column headers for building blank rows
 * @returns New array with rows sorted by invoice number and blank rows
 *          inserted between each invoice group
 */
export function insertInvoiceGroupSeparators(
  exportRows: Record<string, string>[],
  sourceRows: ExportJobRow[],
  invoiceAssignments: Map<string, InvoiceAssignment> | null,
  headers: string[]
): Record<string, string>[] {
  if (!invoiceAssignments || invoiceAssignments.size === 0 || exportRows.length <= 1) {
    return exportRows;
  }

  // Sort by invoice number first so groups are contiguous
  const { sortedExportRows, sortedSourceRows } = sortExportRowsByInvoice(
    exportRows, sourceRows, invoiceAssignments
  );

  // Build a blank row template
  const blankRow: Record<string, string> = {};
  for (const h of headers) {
    blankRow[h] = '';
  }

  const result: Record<string, string>[] = [];
  let prevInvoiceNumber: number | null = null;

  for (let i = 0; i < sortedExportRows.length; i++) {
    // Get the invoice number for this row from the assignments
    const sourceRow = sortedSourceRows[i];
    const assignment = sourceRow ? invoiceAssignments.get(sourceRow.tc_id) : null;
    const currentInvoiceNumber = assignment?.assignedInvoiceNumber ?? null;

    // If the invoice number changed (and we had a previous one), insert a blank row
    if (
      prevInvoiceNumber !== null &&
      currentInvoiceNumber !== null &&
      currentInvoiceNumber !== prevInvoiceNumber
    ) {
      result.push({ ...blankRow });
    }

    result.push(sortedExportRows[i]);
    prevInvoiceNumber = currentInvoiceNumber;
  }

  return result;
}


// ─── Part 17: Insert Blank Rows Between Field-Based Groups ──────────

/**
 * Part 17: Generalized blank-row separator that works for ANY export,
 * not just those with invoice assignments.
 *
 * Groups rows by the value of `groupByField` on the source ExportJobRow,
 * and inserts a blank row between consecutive groups. Unlike
 * `insertInvoiceGroupSeparators`, this does NOT sort the rows — it
 * respects whatever order the caller provides. This keeps behaviour
 * predictable for templates that rely on date-based or other
 * pre-sorted orderings.
 *
 * If you want grouping by invoice number, continue to use
 * `insertInvoiceGroupSeparators` (which also sorts).
 *
 * @param exportRows    - The built export rows (after buildExportRows)
 * @param sourceRows    - The original ExportJobRow[] in the same 1:1 order
 * @param groupByField  - Field name on ExportJobRow to group by
 *                        (e.g. 'account_id', 'pu_date', 'passenger_name')
 * @param headers       - Column headers for building blank rows
 * @returns New array with blank rows inserted between groups
 */
export function insertFieldGroupSeparators(
  exportRows: Record<string, string>[],
  sourceRows: ExportJobRow[],
  groupByField: string,
  headers: string[]
): Record<string, string>[] {
  if (!groupByField || exportRows.length <= 1) {
    return exportRows;
  }

  // Build a blank row template
  const blankRow: Record<string, string> = {};
  for (const h of headers) {
    blankRow[h] = '';
  }

  const getGroupKey = (row: ExportJobRow | undefined): string => {
    if (!row) return '';
    const raw = (row as any)[groupByField];
    if (raw === null || raw === undefined) return '';
    return String(raw);
  };

  const result: Record<string, string>[] = [];
  let prevKey: string | null = null;

  for (let i = 0; i < exportRows.length; i++) {
    const currentKey = getGroupKey(sourceRows[i]);

    // Insert a blank row when the group key changes — but only after the
    // first data row (we don't prepend a leading blank).
    if (prevKey !== null && currentKey !== prevKey) {
      result.push({ ...blankRow });
    }

    result.push(exportRows[i]);
    prevKey = currentKey;
  }

  return result;
}


// ─── Runtime Export Validation ───────────────────────────────────────

/**
 * Run a comprehensive validation pass before export.
 * Returns blocking errors and non-blocking warnings.
 */
export function validateExport(
  rows: ExportJobRow[],
  columns: ExportTemplateColumn[],
  exportRows: Record<string, string>[],
  settings: ExportSettings,
  invoiceAssignments: Map<string, InvoiceAssignment> | null,
  anomalyResults: Map<string, RowAnomalyResult> | null,
  anomalySummary: AnomalySummary | null
): ValidationResult {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  // Use exportable columns (excluding excludeFromExport) for validation
  const exportableCols = getExportableColumns(columns);
  const allEnabledCols = getAllEnabledColumns(columns);
  const excludedCount = allEnabledCols.length - exportableCols.length;

  // 1. Empty export check
  if (exportRows.length === 0) {
    errors.push({
      severity: 'error',
      code: 'EMPTY_EXPORT',
      message: 'No rows to export',
      detail: 'After applying filters and export scope, no rows remain. Adjust your filters or scope.',
    });
  }

  // 2. No enabled columns
  if (exportableCols.length === 0) {
    errors.push({
      severity: 'error',
      code: 'NO_COLUMNS',
      message: 'No enabled template columns',
      detail: 'Enable at least one column in the template builder (columns marked "Exclude from Export" do not count).',
    });
  }

  // 3. Info about excluded columns
  if (excludedCount > 0) {
    warnings.push({
      severity: 'warning',
      code: 'EXCLUDED_COLUMNS',
      message: `${excludedCount} column${excludedCount !== 1 ? 's' : ''} excluded from export output`,
      detail: `${excludedCount} column${excludedCount !== 1 ? 's are' : ' is'} marked as "Exclude from Export" and will not appear in the output file. They are used as filters/criteria only.`,
    });
  }

  // 4. Missing required template columns
  const requiredCols = exportableCols.filter(c => c.is_required);
  if (requiredCols.length > 0 && exportRows.length > 0) {
    for (const col of requiredCols) {
      let blankCount = 0;
      for (const row of exportRows) {
        const val = row[col.export_column_name];
        if (!val || val.trim() === '') blankCount++;
      }
      if (blankCount > 0) {
        const pct = Math.round((blankCount / exportRows.length) * 100);
        if (blankCount === exportRows.length) {
          errors.push({
            severity: 'error',
            code: 'REQUIRED_ALL_BLANK',
            message: `Required column "${col.export_column_name}" is blank in all rows`,
            detail: `This column is marked as required but resolves to blank for every row. Check the source field mapping.`,
          });
        } else {
          warnings.push({
            severity: 'warning',
            code: 'REQUIRED_PARTIAL_BLANK',
            message: `Required column "${col.export_column_name}" is blank in ${blankCount} rows (${pct}%)`,
            detail: `${blankCount} of ${exportRows.length} rows have a blank value for this required column.`,
          });
        }
      }
    }
  }

  // 5. Missing invoice numbers
  const invoiceFields = ['assigned_invoice_number', 'invoice_group_key', 'invoice_passenger_name'];
  const hasInvoiceColumns = exportableCols.some(c =>
    c.source_type === 'source_field' && c.source_field && invoiceFields.includes(c.source_field)
  );
  if (hasInvoiceColumns && (!invoiceAssignments || invoiceAssignments.size === 0)) {
    errors.push({
      severity: 'error',
      code: 'MISSING_INVOICES',
      message: 'Template uses invoice fields but no invoices are assigned',
      detail: 'Assign invoice numbers in the Invoice Run Settings panel before exporting.',
    });
  }

  // 6. Invalid numeric fields check (sample first 50 rows)
  const numericHintCols = exportableCols.filter(c => {
    const field = c.source_field || '';
    return ['grand_total', 'trip_total', 'paid_amount', 'cash_amt', 'eftpos_amt',
      'totalmobility_amt', 'tm_hoist_amt', 'account_amt', 'net_after_tm',
      'trip_base_fare', 'job_km', 'discount', 'other_amt', 'ecard_amt',
      'direct_settled', 'assigned_invoice_number'].includes(field);
  });

  if (numericHintCols.length > 0 && exportRows.length > 0) {
    const sampleSize = Math.min(50, exportRows.length);
    for (const col of numericHintCols) {
      let invalidCount = 0;
      for (let i = 0; i < sampleSize; i++) {
        const val = exportRows[i][col.export_column_name];
        if (val && val.trim() !== '' && isNaN(Number(val))) {
          invalidCount++;
        }
      }
      if (invalidCount > 0) {
        warnings.push({
          severity: 'warning',
          code: 'INVALID_NUMERIC',
          message: `Column "${col.export_column_name}" has ${invalidCount} non-numeric values in sample`,
          detail: `Expected numeric values based on source field "${col.source_field}". Found text in ${invalidCount} of ${sampleSize} sampled rows.`,
        });
      }
    }
  }

  // 7. Unreviewed anomalies warning
  if (anomalySummary && anomalySummary.unreviewed > 0 && settings.scope === 'all') {
    warnings.push({
      severity: 'warning',
      code: 'UNREVIEWED_ANOMALIES',
      message: `${anomalySummary.unreviewed} rows have unreviewed anomalies`,
      detail: `You are exporting all rows including ${anomalySummary.unreviewed} with unreviewed anomaly flags. Consider reviewing them or changing the export scope.`,
    });
  }

  // 8. File name validation
  if (!settings.fileName || settings.fileName.trim() === '') {
    warnings.push({
      severity: 'warning',
      code: 'NO_FILENAME',
      message: 'No file name specified',
      detail: 'A default file name will be used.',
    });
  }

  // 9. Blank row separator info
  if (settings.blankRowBetweenInvoices) {
    if (!invoiceAssignments || invoiceAssignments.size === 0) {
      warnings.push({
        severity: 'warning',
        code: 'BLANK_ROWS_NO_INVOICES',
        message: 'Blank row separators enabled but no invoices assigned',
        detail: 'Blank rows between invoice groups require invoice numbers to be assigned first. No blank rows will be inserted.',
      });
    }
  }

  const hasBlockingErrors = errors.length > 0;

  return {
    isValid: !hasBlockingErrors,
    hasBlockingErrors,
    errors,
    warnings,
    finalRowCount: exportRows.length,
    finalColumnCount: exportableCols.length,
  };
}

// ─── CSV Generation ─────────────────────────────────────────────────

/**
 * Escape a value for CSV output.
 * Wraps in quotes if the value contains commas, quotes, or newlines.
 * Doubles any existing quotes.
 */
function escapeCSVValue(val: string): string {
  if (!val) return '';
  // Check if quoting is needed
  if (val.includes(',') || val.includes('"') || val.includes('\n') || val.includes('\r')) {
    return '"' + val.replace(/"/g, '""') + '"';
  }
  return val;
}

/**
 * Generate a CSV string from export rows.
 * Handles proper escaping of commas, quotes, and line breaks.
 * Outputs UTF-8 compatible content.
 */
export function generateCSV(
  headers: string[],
  exportRows: Record<string, string>[],
  includeHeaders: boolean = true
): string {
  const lines: string[] = [];

  if (includeHeaders) {
    lines.push(headers.map(h => escapeCSVValue(h)).join(','));
  }

  for (const row of exportRows) {
    const values = headers.map(h => {
      const raw = row[h] || '';
      return escapeCSVValue(parseExcelFormulaMarker(raw) ? '' : raw);
    });
    lines.push(values.join(','));
  }

  return lines.join('\r\n');
}

// ─── XLSX Generation ────────────────────────────────────────────────

/**
 * Generate an XLSX workbook from export rows.
 * Returns a Uint8Array buffer ready for download.
 */
export function generateXLSX(
  headers: string[],
  exportRows: Record<string, string>[],
  sheetName: string = 'Export',
  includeHeaders: boolean = true
): Uint8Array {
  const data: any[][] = [];

  if (includeHeaders) {
    data.push(headers);
  }

  for (const row of exportRows) {
    const rowData = headers.map(h => {
      const val = row[h] || '';
      if (parseExcelFormulaMarker(val)) {
        return '';
      }
      if (val !== '' && !isNaN(Number(val)) && String(val).trim() !== '') {
        return Number(val);
      }
      return val;
    });
    data.push(rowData);
  }

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  const headerLetterMap = buildHeaderLetterMap(headers);

  exportRows.forEach((row, rowIdx) => {
    const excelRowNumber = includeHeaders ? rowIdx + 2 : rowIdx + 1;

    headers.forEach((header, colIdx) => {
      const raw = row[header] || '';
      const marker = parseExcelFormulaMarker(raw);
      if (!marker) return;

      const cellAddress = `${columnIndexToExcelLetter(colIdx)}${excelRowNumber}`;
      const formula = resolveExcelFormulaTemplate(marker.template, excelRowNumber, headerLetterMap);

      ws[cellAddress] = {
        t: marker.resultType === 'string' ? 'str' : 'n',
        f: formula,
      } as XLSX.CellObject;
    });
  });

  ws['!cols'] = headers.map(h => ({
    wch: Math.min(40, Math.max(10, h.length + 4)),
  }));

  XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31));
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Uint8Array(buffer);
}

// ─── Browser Download ───────────────────────────────────────────────

/**
 * Trigger a browser download for a file.
 */
export function downloadFile(
  content: string | Uint8Array,
  fileName: string,
  mimeType: string
): void {
  let blob: Blob;
  if (typeof content === 'string') {
    // Add BOM for UTF-8 CSV files
    const bom = '\uFEFF';
    blob = new Blob([bom + content], { type: mimeType });
  } else {
    blob = new Blob([content], { type: mimeType });
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ─── Default File Name Generator ────────────────────────────────────

/**
 * Generate a default file name based on template name, date, and row count.
 * Pattern: {template_name}_{YYYY-MM-DD}_{row_count}
 */
export function getDefaultFileName(
  templateName: string,
  rowCount: number,
  fileType: ExportFileType
): string {
  const sanitized = templateName
    .replace(/[^a-zA-Z0-9\s_-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);

  const today = new Date().toISOString().slice(0, 10);
  return `${sanitized}_${today}_${rowCount}.${fileType}`;
}

/**
 * Get just the base name (without extension) for the file name input.
 */
export function getDefaultFileBaseName(
  templateName: string,
  rowCount: number
): string {
  const sanitized = templateName
    .replace(/[^a-zA-Z0-9\s_-]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);

  const today = new Date().toISOString().slice(0, 10);
  return `${sanitized}_${today}_${rowCount}`;
}

// ─── Build Export Summary ───────────────────────────────────────────

/**
 * Build a comprehensive export summary for the review panel.
 */
export function buildExportSummary(
  templateName: string,
  settings: ExportSettings,
  exportRows: Record<string, string>[],
  columns: ExportTemplateColumn[],
  rows: ExportJobRow[],
  invoiceSummary: InvoiceRunSummary | null,
  marginRules: MarginRule[],
  anomalyResults: Map<string, RowAnomalyResult> | null,
  excludedCount: number
): ExportSummaryData {
  // Use exportable columns count (excluding excludeFromExport)
  const exportableCols = getExportableColumns(columns);
  const activeMarginCount = marginRules.filter(r => r.is_active).length;

  let totalGT = 0;
  for (const row of rows) {
    totalGT += Number(row.grand_total || 0);
  }

  let anomalyIncluded = 0;
  if (anomalyResults) {
    for (const row of rows) {
      if (anomalyResults.has(row.tc_id)) anomalyIncluded++;
    }
  }

  return {
    templateName,
    fileType: settings.fileType,
    fileName: `${settings.fileName}.${settings.fileType}`,
    rowCount: exportRows.length,
    columnCount: exportableCols.length,
    startingInvoice: invoiceSummary?.startingNumber || null,
    endingInvoice: invoiceSummary?.endingNumber || null,
    invoiceGroupCount: invoiceSummary?.uniqueGroups || null,
    activeMarginRules: activeMarginCount,
    anomalyIncluded,
    anomalyExcluded: excludedCount,
    totalGrandTotal: totalGT,
    scope: settings.scope,
  };
}

// ─── Full Export Pipeline ───────────────────────────────────────────

/**
 * Execute the full export pipeline:
 * 1. Apply export scope filter
 * 2. Build export rows
 * 3. Optionally insert blank rows between invoice groups
 * 4. Validate
 * 5. Generate file (CSV or XLSX)
 * 6. Trigger download
 *
 * Returns the validation result and summary.
 */
export function executeExport(
  rows: ExportJobRow[],
  columns: ExportTemplateColumn[],
  aliasRules: AddressAliasRule[],
  marginRules: MarginRule[],
  invoiceAssignments: Map<string, InvoiceAssignment> | null,
  invoiceSummary: InvoiceRunSummary | null,
  anomalyResults: Map<string, RowAnomalyResult> | null,
  anomalySummary: AnomalySummary | null,
  settings: ExportSettings,
  templateName: string
): {
  validation: ValidationResult;
  summary: ExportSummaryData;
  success: boolean;
} {
  // 1. Apply scope
  const { included, excluded } = applyExportScope(rows, settings.scope, anomalyResults);

  // 2. Build export rows
  const headers = getExportHeaders(columns);
  let exportRows = buildExportRows(included, columns, aliasRules, marginRules, invoiceAssignments);

  // 3. Validate (before inserting blank rows — validation counts data rows only)
  const validation = validateExport(
    included, columns, exportRows, settings,
    invoiceAssignments, anomalyResults, anomalySummary
  );

  // 4. Build summary (before inserting blank rows)
  const summary = buildExportSummary(
    templateName, settings, exportRows, columns, included,
    invoiceSummary, marginRules, anomalyResults, excluded
  );

  // 5. Sort by invoice number and optionally insert blank rows between groups
  if (invoiceAssignments && invoiceAssignments.size > 0) {
    if (settings.blankRowBetweenInvoices) {
      // Sort + insert blank rows between invoice groups
      exportRows = insertInvoiceGroupSeparators(exportRows, included, invoiceAssignments, headers);
    } else {
      // Sort by invoice number only (no blank rows)
      const { sortedExportRows } = sortExportRowsByInvoice(exportRows, included, invoiceAssignments);
      exportRows = sortedExportRows;
    }
  }


  // 6. If valid, generate and download
  if (validation.isValid) {
    const fullFileName = `${settings.fileName}.${settings.fileType}`;

    if (settings.fileType === 'csv') {
      const csvContent = generateCSV(headers, exportRows, settings.includeHeaders);
      downloadFile(csvContent, fullFileName, 'text/csv;charset=utf-8');
    } else {
      const xlsxBuffer = generateXLSX(headers, exportRows, settings.sheetName, settings.includeHeaders);
      downloadFile(xlsxBuffer, fullFileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    }
  }

  return {
    validation,
    summary,
    success: validation.isValid,
  };
}
